import { Node, mergeAttributes } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    footnote: {
      /**
       * 현재 cursor 위치에 footnote 마커 인서트. 새 id 발급 후 부모 form 에 전파.
       */
      insertFootnote: (id: string) => ReturnType;
    };
  }
}

/**
 * 본문 안 각주 inline 마커.
 *
 * - HTML 출력: `<sup data-footnote-id="...">[N]</sup>` 형태 (N 은 어드민 표시 숫자, 백엔드 저장값은 id)
 * - parse 시 같은 attribute 보존
 * - 백엔드 sanitize-options 의 sup 화이트리스트와 정합
 *
 * Tiptap 표준 footnote extension 이 없어 직접 정의. content 자체는 본문 안에 두지
 * 않고 (위치 anchor 만 박힘), 별도 footnotes 배열로 form 에서 관리.
 */
export const FootnoteNode = Node.create({
  name: "footnote",
  inline: true,
  group: "inline",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      footnoteId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-footnote-id"),
        renderHTML: (attributes) => {
          if (!attributes.footnoteId) return {};
          return { "data-footnote-id": attributes.footnoteId };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "sup[data-footnote-id]",
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    // 어드민 화면에서는 전체 id 를 마커 텍스트로 노출 — prefix 4자만 쓰면 서로 다른
    // 각주가 동일 prefix 가질 가능성이 있어 운영자가 추적할 때 헷갈린다.
    // 위키 단에서는 footnotes 배열 순서 기준 [1][2] 번호로 다시 부여하므로 표시 길이 영향 없음.
    return [
      "sup",
      mergeAttributes(HTMLAttributes, { class: "handbook-footnote-marker" }),
      `[${node.attrs.footnoteId ?? "fn"}]`,
    ];
  },

  addCommands() {
    return {
      insertFootnote:
        (id: string) =>
        ({ chain, state }) => {
          // selection 을 그대로 두면 insertContent 가 선택 영역을 replace 한다.
          // "asdf" 선택 후 ✱ → "asdf" 가 [fn] 으로 바뀌어버림.
          // 의도된 동작은 selection 유지 + 그 끝(우측) 에 마커 추가 →
          // cursor 를 selection.to 로 collapse 한 다음 insert.
          const end = state.selection.to;
          return chain()
            .setTextSelection(end)
            .insertContent({
              type: this.name,
              attrs: { footnoteId: id },
            })
            .run();
        },
    };
  },
});
